import { OperationReportTypeEnum } from '@shared/api/types';
import { ChartConfig } from '@shared/components/ui/chart';
import { formatNumber } from '@shared/lib/utils';
import { YAxisProps } from 'recharts';
import { generateEvenTicks } from './utils';

export const packagingChartConfig = {
  packaging: {
    label: 'Packaging',
    color: 'var(--chart-packaging)',
    unit: 'tCO2e',
  },
};

export const emissionsChartConfig = {
  emissionsTonnes: {
    label: 'Emissions',
    color: 'var(--chart-3)',
    unit: 'tCO2e',
  },
};

export const carbonIntensityChartConfig = {
  carbonIntensity: {
    label: 'Carbon Intensity',
    color: 'var(--chart-carbon-intensity)',
    unit: 'kgCO2e/kg',
  },
};

export const totalWeightKgChartConfig = (
  reportType: OperationReportTypeEnum
) => ({
  totalWeightKg: {
    label:
      reportType === OperationReportTypeEnum.purchase
        ? 'Purchased weight'
        : 'Sold weight',
    color: 'var(--chart-weight)',
    unit: 'tonnes',
  },
});

export const foodAndDrinkChartConfig = {
  'food and drink': {
    label: 'Food & Drink',
    color: 'var(--chart-food-and-drink)',
    unit: 'tCO2e',
  },
};

export const emissionsWithCarbonIntensityConfig = {
  ...emissionsChartConfig,
  ...carbonIntensityChartConfig,
};

export const emissionsWithGoodsTypeConfig = {
  ...foodAndDrinkChartConfig,
  ...packagingChartConfig,
} satisfies ChartConfig;

export const spendChartConfig = (currencySymbol: string) =>
  ({
    totalSpend: {
      label: 'Total spend',
      color: 'var(--chart-cost)',
      unit: currencySymbol,
    },
  }) satisfies ChartConfig;

export const emissionsWithSpendConfig = (currencySymbol: string) =>
  ({
    ...emissionsChartConfig,
    ...spendChartConfig(currencySymbol),
  }) satisfies ChartConfig;

export function spendAxisProps(
  dataMax: number,
  currencySymbol: string
): YAxisProps {
  const ticks = generateEvenTicks(dataMax);
  return {
    label: {
      value: currencySymbol,
      position: 'top',
      offset: 25,
      fontSize: 12,
      fill: 'var(--chart-cost)',
    },
    domain: [0, ticks[ticks.length - 1]],
    ticks,
    axisLine: true,
    tickFormatter: (value) => formatNumber(value),
  };
}

export function totalWeightKgAxisProps(
  dataMax: number,
  totalEmissionsTonnes?: number
): YAxisProps {
  const ticks = generateEvenTicks(totalEmissionsTonnes ?? dataMax);
  return {
    label: {
      value: 'tonnes',
      position: 'top',
      offset: 25,
      fontSize: 12,
      fill: 'var(--chart-weight)',
    },
    domain: [0, ticks[ticks.length - 1]],
    ticks,
    axisLine: true,
    tickFormatter: (value) => {
      return formatNumber(value);
    },
  };
}

export function carbonIntensityAxisProps(dataMax: number): YAxisProps {
  const ticks = generateEvenTicks(dataMax);
  return {
    label: {
      value: 'kgCO2e/kg',
      position: 'top',
      offset: 25,
      fontSize: 12,
      fill: 'var(--chart-carbon-intensity)',
    },
    domain: [0, ticks[ticks.length - 1]],
    ticks,
    axisLine: true,
    tickFormatter: (value) => formatNumber(value),
  };
}

export function totalEmissionsAxisProps(dataMax: number): YAxisProps {
  const ticks = generateEvenTicks(dataMax);
  return {
    label: {
      value: 'tCO2e',
      position: 'top',
      offset: 25,
      fontSize: 12,
      fill: 'var(--chart-3)',
    },
    domain: [0, ticks[ticks.length - 1]],
    ticks,
    allowDataOverflow: true,
    axisLine: true,
    tickFormatter: (value) => {
      return value;
    },
  };
}
